import React from 'react'
import { Row, Col, Card, Statistic, Alert, Spin, Tag, Divider, Typography, Button, message, Tabs, Tooltip, Table } from 'antd'
import { ArrowUpOutlined, ArrowDownOutlined, DollarOutlined, ApiOutlined, BookOutlined, CopyOutlined, CodeOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { api } from '@services/api'

const { Title, Text, Paragraph } = Typography
const { TabPane } = Tabs

const Dashboard: React.FC = () => {
  // 获取统计概览数据
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['statistics', 'overview'],
    queryFn: api.statistics.getOverview,
    refetchInterval: 30000, // 30秒刷新一次
  })

  // 获取系统状态
  const { data: systemStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['monitoring', 'status'],
    queryFn: api.monitoring.getSystemStatus,
    refetchInterval: 10000, // 10秒刷新一次
  })

  // 获取币安下单 API 延迟（10分钟刷新一次）
  const { data: binanceOrderLatency } = useQuery({
    queryKey: ['monitoring', 'binance', 'order-latency'],
    queryFn: api.monitoring.getBinanceOrderLatency,
    staleTime: 10 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  })

  // 获取风险指标
  const { data: riskMetrics, isLoading: riskLoading } = useQuery({
    queryKey: ['risk', 'metrics'],
    queryFn: api.risk.getSimpleMetrics,
    refetchInterval: 15000, // 15秒刷新一次
  })

  // 获取交易所详细信息
  const { data: exchangesInfo, isLoading: exchangesLoading } = useQuery({
    queryKey: ['monitoring', 'exchanges'],
    queryFn: api.monitoring.getExchangesInfo,
    refetchInterval: 30000, // 30秒刷新一次
  })

  const { data: tokenList } = useQuery({
    queryKey: ['tokens-dashboard'],
    queryFn: () => api.token.list(),
    refetchInterval: 30000,
  })

  const { data: dynamicRiskStatus } = useQuery({
    queryKey: ['dynamic-risk-status-dashboard'],
    queryFn: () => api.dynamicRisk.getStatus(),
    refetchInterval: 30000,
  })

  const systemData = systemStatus?.data || systemStatus
  const latencyData = binanceOrderLatency?.data || binanceOrderLatency
  const monthOverview = overview?.data?.month_overview || {}

  if (overviewLoading || statusLoading || riskLoading || exchangesLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="page-inner">
      {/* 系统状态告警 */}
      {riskMetrics?.warnings && riskMetrics.warnings.length > 0 && (
        <Alert
          message="风险提醒"
          description={riskMetrics.warnings.join(', ')}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {(dynamicRiskStatus?.dashboard_alerts || []).length > 0 && (
        <Alert
          message="动态风控生效中"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {(dynamicRiskStatus.dashboard_alerts || []).map((item: string, idx: number) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          }
          type="error"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {(dynamicRiskStatus?.active_rule_count || 0) === 0 &&
        (dynamicRiskStatus?.triggered_rule_count || 0) > 0 && (
        <Alert
          message="动态风控条件成立"
          description="有条件被触发，可在「动态风控」页面查看详情。"
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 核心指标卡片 */}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="今日盈亏"
              value={riskMetrics?.daily_pnl || 0}
              precision={2}
              valueStyle={{ 
                color: (riskMetrics?.daily_pnl || 0) >= 0 ? '#3f8600' : '#cf1322',
                fontSize: 24,
                fontWeight: 'bold'
              }}
              prefix={
                (riskMetrics?.daily_pnl || 0) >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />
              }
              suffix="USDT"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="账户余额"
              value={riskMetrics?.account_balance || 0}
              precision={2}
              valueStyle={{ fontSize: 24, fontWeight: 'bold' }}
              suffix="USDT"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={
                <span>
                  风险评分
                  <Tooltip title="0 表示风险最低，100 表示风险最高（数值越低越安全）">
                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                  </Tooltip>
                </span>
              }
              value={riskMetrics?.risk_score || 0}
              precision={0}
              valueStyle={{ 
                color: (riskMetrics?.risk_score || 0) < 30 ? '#3f8600' : 
                       (riskMetrics?.risk_score || 0) < 60 ? '#faad14' : '#cf1322',
                fontSize: 24,
                fontWeight: 'bold'
              }}
              suffix="/ 100"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              // Backend margin_ratio is "margin_used / account_balance" (0..1),
              // so show it as a percentage to avoid misleading "x" display.
              title="保证金占用率"
              value={(riskMetrics?.margin_ratio || 0) * 100}
              precision={2}
              valueStyle={{ 
                color: (riskMetrics?.margin_ratio || 0) < 0.1 ? '#3f8600' :
                       (riskMetrics?.margin_ratio || 0) < 0.3 ? '#faad14' : '#cf1322',
                fontSize: 24,
                fontWeight: 'bold'
              }}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>

      {/* 详细信息区域 */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16}>
          <Card title="交易概览" size="small">
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Statistic
                  title="当前实际杠杆"
                  value={monthOverview?.current_leverage ?? riskMetrics?.leverage_usage ?? 0}
                  precision={2}
                  suffix="x"
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="当前余额"
                  value={monthOverview?.current_balance ?? riskMetrics?.account_balance ?? 0}
                  precision={2}
                  suffix="USDT"
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="月度交易"
                  value={monthOverview?.monthly_trades ?? 0}
                  suffix="笔"
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
            </Row>
            
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col xs={24} sm={8}>
                <Statistic
                  title="平均持仓时间"
                  value={monthOverview?.avg_holding_hours ?? 0}
                  precision={1}
                  suffix="小时"
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="月度利润"
                  value={monthOverview?.monthly_profit ?? 0}
                  precision={2}
                  suffix="USDT"
                  valueStyle={{ 
                    fontSize: 16,
                    color: (monthOverview?.monthly_profit ?? 0) >= 0 ? '#3f8600' : '#cf1322'
                  }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="月度利润率"
                  value={monthOverview?.monthly_profit_rate ?? 0}
                  precision={2}
                  suffix="%"
                  valueStyle={{ 
                    fontSize: 16,
                    color: (monthOverview?.monthly_profit_rate ?? 0) >= 0 ? '#3f8600' : '#cf1322'
                  }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="系统状态" size="small">
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Binance 下单延迟</span>
                <span>
                  {typeof latencyData?.latency_ms === 'number' ? (
                    <Tag color={latencyData?.ok ? 'green' : 'orange'}>
                      {Math.round(latencyData.latency_ms)} ms
                    </Tag>
                  ) : (
                    <Tag color="red">不可用</Tag>
                  )}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>数据库</span>
                <span style={{ color: systemData?.database_status === 'healthy' ? '#52c41a' : '#ff4d4f' }}>
                  {systemData?.database_status === 'healthy' ? '正常' : '异常'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Redis缓存</span>
                <span style={{ color: systemData?.redis_status === 'healthy' ? '#52c41a' : '#ff4d4f' }}>
                  {systemData?.redis_status === 'healthy' ? '正常' : '异常'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>CPU使用率</span>
                <span>{systemData?.cpu_usage?.toFixed(1) || 0}%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>内存使用率</span>
                <span>{systemData?.memory_usage?.toFixed(1) || 0}%</span>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 交易所状态 */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>交易所状态</span>
                {exchangesInfo?.data?.summary && (
                  <div style={{ fontSize: 12, fontWeight: 'normal', color: '#666' }}>
                    总余额: ${exchangesInfo.data.summary.total_balance_usdt?.toFixed(2) || '0.00'} USDT
                  </div>
                )}
              </div>
            } 
            size="small"
          >
            <Row gutter={16}>
              {exchangesInfo?.data?.exchanges && Object.entries(exchangesInfo.data.exchanges).map(([exchange, info]: [string, any]) => {
                // 只显示已配置的交易所
                if (!info.configured) {
                  return null;
                }
                
                return (
                  <Col key={exchange} xs={24} sm={12} lg={8}>
                    <Card 
                      size="small"
                      style={{ 
                        marginBottom: 16,
                        border: `1px solid ${info.connected ? '#d9f7be' : '#ffccc7'}`,
                        backgroundColor: info.connected ? '#f6ffed' : '#fff2f0'
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        {/* 交易所名称和状态 */}
                        <div style={{ 
                          fontSize: 16, 
                          fontWeight: 'bold', 
                          textTransform: 'uppercase',
                          marginBottom: 8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8
                        }}>
                          <ApiOutlined />
                          {exchange}
                          {info.balance?.testnet && <Tag color="orange">测试网</Tag>}
                        </div>
                        
                        {/* 连接状态 */}
                        <div style={{ marginBottom: 12 }}>
                          <Tag color={info.connected ? 'success' : 'error'}>
                            {info.status}
                          </Tag>
                        </div>
                        
                        {/* 余额信息 */}
                        {info.connected && info.balance ? (
                          <div>
                            <Divider style={{ margin: '8px 0' }} />
                            
                            {/* 总资产 */}
                            <div style={{ 
                              fontSize: 14, 
                              fontWeight: 'bold',
                              color: '#1890ff',
                              marginBottom: 12,
                              textAlign: 'center'
                            }}>
                              <DollarOutlined /> 总资产: ${info.balance.total_balance_usdt || 0}
                            </div>
                            
                            {/* 现货 vs 合约对比 */}
                            <Row gutter={8} style={{ marginBottom: 12 }}>
                              <Col span={12}>
                                <div style={{ 
                                  padding: '6px 8px',
                                  backgroundColor: '#e6f7ff',
                                  borderRadius: '4px',
                                  textAlign: 'center'
                                }}>
                                  <div style={{ fontSize: 11, fontWeight: 'bold', color: '#1890ff' }}>现货账户</div>
                                  <div style={{ fontSize: 12, fontWeight: 'bold' }}>
                                    ${info.balance.spot_balance_usdt || 0}
                                  </div>
                                </div>
                              </Col>
                              <Col span={12}>
                                <div style={{ 
                                  padding: '6px 8px',
                                  backgroundColor: '#fff7e6',
                                  borderRadius: '4px',
                                  textAlign: 'center'
                                }}>
                                  <div style={{ fontSize: 11, fontWeight: 'bold', color: '#fa8c16' }}>合约账户</div>
                                  <div style={{ fontSize: 12, fontWeight: 'bold' }}>
                                    ${info.balance.futures_balance_usdt || 0}
                                  </div>
                                </div>
                              </Col>
                            </Row>
                            
                            {/* 可用资金状态 */}
                            <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                <span>总可用:</span>
                                <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                  ${info.balance.total_free_usdt || 0}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>总占用:</span>
                                <span style={{ color: '#faad14', fontWeight: 'bold' }}>
                                  ${info.balance.total_used_usdt || 0}
                                </span>
                              </div>
                            </div>
                            
                            {/* 持仓信息 */}
                            {info.balance.position_count > 0 && (
                              <div style={{ 
                                padding: '6px 8px',
                                backgroundColor: info.balance.total_unrealized_pnl >= 0 ? '#f6ffed' : '#fff2f0',
                                borderRadius: '4px',
                                marginBottom: 8
                              }}>
                                <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ fontWeight: 'bold' }}>持仓数量:</span>
                                  <span>{info.balance.position_count} 个</span>
                                </div>
                                <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between' }}>
                                  <span>未实现盈亏:</span>
                                  <span style={{ 
                                    color: info.balance.total_unrealized_pnl >= 0 ? '#52c41a' : '#ff4d4f',
                                    fontWeight: 'bold'
                                  }}>
                                    {info.balance.total_unrealized_pnl > 0 ? '+' : ''}${info.balance.total_unrealized_pnl}
                                  </span>
                                </div>
                              </div>
                            )}
                            
                            {/* 持仓详情（如果有的话） */}
                            {info.balance.position_summary && info.balance.position_summary.length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                <div style={{ fontSize: 10, color: '#999', marginBottom: 4, fontWeight: 'bold' }}>
                                  活跃持仓:
                                </div>
                                <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                                  {info.balance.position_summary.slice(0, 3).map((pos: any, idx: number) => (
                                    <div key={idx} style={{ 
                                      fontSize: 9, 
                                      backgroundColor: '#fafafa', 
                                      padding: '2px 4px', 
                                      marginBottom: '2px',
                                      borderRadius: '2px',
                                      display: 'flex',
                                      justifyContent: 'space-between'
                                    }}>
                                      <span>{pos.symbol} {pos.side}</span>
                                      <span style={{ 
                                        color: pos.unrealized_pnl >= 0 ? '#52c41a' : '#ff4d4f',
                                        fontWeight: 'bold'
                                      }}>
                                        {pos.unrealized_pnl > 0 ? '+' : ''}${pos.unrealized_pnl}
                                      </span>
                                    </div>
                                  ))}
                                  {info.balance.position_summary.length > 3 && (
                                    <div style={{ fontSize: 9, color: '#999', textAlign: 'center' }}>
                                      ...还有 {info.balance.position_summary.length - 3} 个持仓
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* 现货币种详情 */}
                            {info.balance.spot_details && Object.keys(info.balance.spot_details).length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                <div style={{ fontSize: 10, color: '#999', marginBottom: 4, fontWeight: 'bold' }}>
                                  现货币种:
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                  {Object.entries(info.balance.spot_details).map(([coin, balance]: [string, any]) => (
                                    <Tag key={coin} color="blue">
                                      {coin}: ${balance.total}
                                    </Tag>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* 合约币种详情 */}
                            {info.balance.futures_details && Object.keys(info.balance.futures_details).length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                <div style={{ fontSize: 10, color: '#999', marginBottom: 4, fontWeight: 'bold' }}>
                                  合约币种:
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                  {Object.entries(info.balance.futures_details).map(([coin, balance]: [string, any]) => (
                                    <Tag key={coin} color="orange">
                                      {coin}: ${balance.total}
                                    </Tag>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : !info.connected && info.error ? (
                          <div style={{ fontSize: 12, color: '#ff4d4f', marginTop: 8 }}>
                            {info.message}
                          </div>
                        ) : null}
                        
                        {/* 最后更新时间 */}
                        {info.account_info?.last_updated && (
                          <div style={{ 
                            fontSize: 10, 
                            color: '#ccc', 
                            marginTop: 8,
                            borderTop: '1px solid #f0f0f0',
                            paddingTop: 4
                          }}>
                            更新于: {new Date(info.account_info.last_updated).toLocaleTimeString()}
                          </div>
                        )}
                      </div>
                    </Card>
                  </Col>
                );
              })}
              
              {/* 如果没有配置任何交易所 */}
              {(!exchangesInfo?.data?.exchanges || 
                Object.values(exchangesInfo.data.exchanges).every((info: any) => !info.configured)) && (
                <Col span={24}>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: 40,
                    color: '#999',
                    backgroundColor: '#fafafa',
                    borderRadius: 6,
                    border: '1px dashed #d9d9d9'
                  }}>
                    <ApiOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                    <div>暂无配置的交易所</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      请前往系统设置配置交易所API密钥
                    </div>
                  </div>
                </Col>
              )}
            </Row>
            
            {/* 汇总信息 */}
            {exchangesInfo?.data?.summary && (
              <div style={{ 
                marginTop: 16, 
                padding: 12,
                backgroundColor: '#f5f5f5',
                borderRadius: 6,
                fontSize: 12
              }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic 
                      title="总计" 
                      value={exchangesInfo.data.summary.total_exchanges} 
                      suffix="个交易所"
                      valueStyle={{ fontSize: 14 }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="已配置" 
                      value={exchangesInfo.data.summary.configured_exchanges} 
                      suffix="个"
                      valueStyle={{ fontSize: 14, color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="已连接" 
                      value={exchangesInfo.data.summary.connected_exchanges} 
                      suffix="个"
                      valueStyle={{ fontSize: 14, color: '#52c41a' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="总余额" 
                      value={exchangesInfo.data.summary.total_balance_usdt || 0}
                      precision={2}
                      prefix={<DollarOutlined />}
                      suffix="USDT"
                      valueStyle={{ fontSize: 14, color: '#faad14' }}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card title="Token交易概览" size="small">
            <Table
              size="small"
              rowKey={(r: any) => String(r.token_id || r.token_name)}
              pagination={false}
              dataSource={(overview?.data?.top_tokens || []).map((row: any) => {
                const token = (tokenList || []).find((t: any) => Number(t.id) === Number(row.token_id))
                return {
                  ...row,
                  runtime_connected: token?.exchange_status?.connected,
                }
              })}
              columns={[
                { title: 'Token', dataIndex: 'token_name', key: 'token_name' },
                { title: '交易数', dataIndex: 'total_trades', key: 'total_trades' },
                { title: '累计PnL', dataIndex: 'total_pnl', key: 'total_pnl' },
                { title: '交易量', dataIndex: 'total_volume', key: 'total_volume' },
                {
                  title: '实例状态',
                  dataIndex: 'runtime_connected',
                  key: 'runtime_connected',
                  render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? '正常' : '异常'}</Tag>,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Webhook使用帮助 */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <BookOutlined />
                <span>Webhook使用指南</span>
                <Tag color="blue">TradingView集成</Tag>
              </div>
            }
            size="small"
          >
            <Tabs defaultActiveKey="basic" size="small">
              <TabPane tab="基础配置" key="basic">
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Title level={5}>
                      <CodeOutlined /> Webhook URL
                    </Title>
                    <div style={{ 
                      background: '#f6f8fa', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginBottom: '12px',
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      border: '1px solid #e1e4e8',
                      position: 'relative'
                    }}>
                      <Text code style={{ fontSize: '14px' }}>
                        {(() => {
                          const origin = window.location.origin
                          // 去掉非标准端口（3000等），使用标准80/443端口
                          const url = new URL(origin)
                          const isNonStandardPort = url.port && url.port !== '80' && url.port !== '443'
                          const cleanOrigin = isNonStandardPort 
                            ? `${url.protocol}//${url.hostname}` 
                            : origin
                          return `${cleanOrigin}/api/v1/webhook`
                        })()}
                      </Text>
                      <Button 
                        type="text" 
                        size="small"
                        icon={<CopyOutlined />}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={() => {
                          const origin = window.location.origin
                          const url = new URL(origin)
                          const isNonStandardPort = url.port && url.port !== '80' && url.port !== '443'
                          const cleanOrigin = isNonStandardPort 
                            ? `${url.protocol}//${url.hostname}` 
                            : origin
                          navigator.clipboard.writeText(`${cleanOrigin}/api/v1/webhook`)
                          message.success('URL已复制到剪贴板')
                        }}
                      >
                        复制
                      </Button>
                    </div>
                    {window.location.port && window.location.port !== '80' && window.location.port !== '443' && (
                      <Alert
                        type="warning"
                        showIcon
                        message="本地开发环境"
                        description={
                          <div style={{ fontSize: 12 }}>
                            <p>当前通过端口 <strong>{window.location.port}</strong> 访问，显示的URL已自动去掉端口号。</p>
                            <p>部署到服务器后，请确保通过 <strong>80</strong>（HTTP）或 <strong>443</strong>（HTTPS）端口访问，TradingView 将通过该地址发送信号。</p>
                          </div>
                        }
                        style={{ marginBottom: 12, fontSize: 12 }}
                      />
                    )}

                    <Title level={5}>安全说明</Title>
                    <Alert
                      type="info"
                      showIcon
                      message="简化验证机制"
                      description={
                        <div>
                          <p>✅ <strong>已启用：</strong>Token身份验证、JSON格式验证</p>
                          <p>❌ <strong>已禁用：</strong>IP白名单、签名验证、频率限制</p>
                          <p>💡 这样确保TradingView动态IP能正常工作</p>
                        </div>
                      }
                      style={{ marginBottom: 16 }}
                    />
                  </Col>

                  <Col xs={24} md={12}>
                    <Title level={5}>
                      <ApiOutlined /> 开仓示例（做多）
                    </Title>
                    <div style={{ 
                      background: '#f0f9eb', 
                      padding: '12px', 
                      borderRadius: '6px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      border: '1px solid #b7eb8f',
                      position: 'relative',
                      marginBottom: 12
                    }}>
                      <Button 
                        type="text" 
                        size="small"
                        icon={<CopyOutlined />}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={() => {
                          const buyExample = `{
  "token": "YOUR_TOKEN_HERE",
  "ticker": "BTCUSDT",
  "action": "buy",
  "contracts": 0.01,
  "price": "market"
}`
                          navigator.clipboard.writeText(buyExample)
                          message.success('开仓示例已复制')
                        }}
                      >
                        复制
                      </Button>
                      <pre style={{ margin: 0, color: '#24292e' }}>
{`{
  "token": "YOUR_TOKEN_HERE",
  "ticker": "BTCUSDT",
  "action": "buy",
  "contracts": 0.01,
  "price": "market"
}`}
                      </pre>
                    </div>

                    <Title level={5}>
                      <ApiOutlined /> 平仓示例（平多）
                    </Title>
                    <div style={{ 
                      background: '#fff2f0', 
                      padding: '12px', 
                      borderRadius: '6px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      border: '1px solid #ffccc7',
                      position: 'relative',
                      marginBottom: 12
                    }}>
                      <Button 
                        type="text" 
                        size="small"
                        icon={<CopyOutlined />}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={() => {
                          const sellExample = `{
  "token": "YOUR_TOKEN_HERE",
  "ticker": "BTCUSDT",
  "action": "sell",
  "contracts": 0.01,
  "price": "market"
}`
                          navigator.clipboard.writeText(sellExample)
                          message.success('平仓示例已复制')
                        }}
                      >
                        复制
                      </Button>
                      <pre style={{ margin: 0, color: '#24292e' }}>
{`{
  "token": "YOUR_TOKEN_HERE",
  "ticker": "BTCUSDT",
  "action": "sell",
  "contracts": 0.01,
  "price": "market"
}`}
                      </pre>
                    </div>

                    <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                      <p><strong>交易逻辑（单向持仓模式）：</strong></p>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        <li><code>action: "buy"</code> → <strong>开多仓</strong>（做多）</li>
                        <li><code>action: "sell"</code> → <strong>平多仓</strong> / <strong>开空仓</strong>（做空）</li>
                        <li>平仓 = 发送<strong>反方向</strong>相同数量的订单</li>
                      </ul>
                      <p style={{ marginTop: 8 }}><strong>必填字段：</strong> <code>token</code>、<code>ticker</code>、<code>action</code>、<code>contracts</code></p>
                    </div>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tab="完整示例" key="complete">
                <Row gutter={16}>
                  <Col xs={24} lg={12}>
                    <Title level={5}>
                      <CodeOutlined /> 市价单（推荐）
                    </Title>
                    <div style={{ 
                      background: '#f6f8fa', 
                      padding: '12px', 
                      borderRadius: '6px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      border: '1px solid #e1e4e8',
                      position: 'relative'
                    }}>
                      <Button 
                        type="text" 
                        size="small"
                        icon={<CopyOutlined />}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={() => {
                          const marketExample = `{
  "token": "YOUR_TOKEN_HERE",
  "ticker": "BTCUSDT",
  "action": "buy",
  "contracts": 0.1,
  "price": "market",
  "sl": 42000,
  "tp": 44000,
  "exchange": "binance",
  "comment": "TradingView信号"
}`
                          navigator.clipboard.writeText(marketExample)
                          message.success('市价单示例已复制')
                        }}
                      >
                        复制
                      </Button>
                      <pre style={{ margin: 0, color: '#24292e' }}>
{`{
  "token": "YOUR_TOKEN_HERE",
  "ticker": "BTCUSDT",
  "action": "buy", 
  "contracts": 0.1,
  "price": "market",
  "sl": 42000,
  "tp": 44000,
  "exchange": "binance",
  "comment": "TradingView信号"
}`}
                      </pre>
                    </div>

                    <Title level={5} style={{ marginTop: 16 }}>
                      <CodeOutlined /> 限价单
                    </Title>
                    <div style={{ 
                      background: '#f6f8fa', 
                      padding: '12px', 
                      borderRadius: '6px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      border: '1px solid #e1e4e8',
                      position: 'relative'
                    }}>
                      <Button 
                        type="text" 
                        size="small"
                        icon={<CopyOutlined />}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={() => {
                          const limitExample = `{
  "token": "YOUR_TOKEN_HERE",
  "ticker": "ETHUSDT",
  "action": "sell",
  "contracts": 1.0,
  "price": 2650.50,
  "sl": 2700,
  "tp": 2600
}`
                          navigator.clipboard.writeText(limitExample)
                          message.success('限价单示例已复制')
                        }}
                      >
                        复制
                      </Button>
                      <pre style={{ margin: 0, color: '#24292e' }}>
{`{
  "token": "YOUR_TOKEN_HERE",
  "ticker": "ETHUSDT",
  "action": "sell",
  "contracts": 1.0,
  "price": 2650.50,
  "sl": 2700,
  "tp": 2600
}`}
                      </pre>
                    </div>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Title level={5}>字段说明</Title>
                    <div style={{ background: '#f9f9f9', padding: 12, borderRadius: 6 }}>
                      <div style={{ marginBottom: 12 }}>
                        <Text strong>必填字段：</Text>
                        <ul style={{ margin: '8px 0', paddingLeft: 16, fontSize: 13 }}>
                          <li><code>token</code> - API Token（请在Token管理中创建）</li>
                          <li><code>ticker</code> - 交易对，如 BTCUSDT、ETHUSDT</li>
                          <li><code>action</code> - 交易动作："buy" 或 "sell"</li>
                          <li><code>contracts</code> - 合约数量（必须大于0）</li>
                        </ul>
                      </div>

                      <div>
                        <Text strong>可选字段：</Text>
                        <ul style={{ margin: '8px 0', paddingLeft: 16, fontSize: 13 }}>
                          <li><code>price</code> - "market"（市价单）或数字（限价单）</li>
                          <li><code>sl</code> - 止损价格</li>
                          <li><code>tp</code> - 止盈价格</li>
                          <li><code>exchange</code> - 指定交易所："binance"、"okx"、"bybit"</li>
                          <li><code>comment</code> - 订单备注</li>
                        </ul>
                      </div>
                    </div>

                    <Alert
                      type="success"
                      showIcon
                      message="使用建议"
                      description={
                        <div style={{ fontSize: 12 }}>
                          <p>🎯 <strong>推荐使用市价单：</strong>执行速度快，成交确定性高</p>
                          <p>🔒 <strong>设置止损止盈：</strong>sl 和 tp 字段可有效控制风险</p>
                          <p>🎛 <strong>指定交易所：</strong>不指定时系统自动选择最优交易所</p>
                        </div>
                      }
                      style={{ marginTop: 16 }}
                    />
                  </Col>
                </Row>
              </TabPane>

              <TabPane tab="TradingView配置" key="tradingview">
                <Row gutter={16}>
                  <Col xs={24} lg={14}>
                    <Title level={5}>Pine Script示例</Title>
                    <div style={{ 
                      background: '#f6f8fa', 
                      padding: '12px', 
                      borderRadius: '6px',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      border: '1px solid #e1e4e8',
                      position: 'relative'
                    }}>
                      <Button 
                        type="text" 
                        size="small"
                        icon={<CopyOutlined />}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={() => {
                          const pineScript = `//@version=5
strategy("TradingBridge Webhook", overlay=true)

// 策略逻辑（示例）
if (ta.crossover(ta.sma(close, 10), ta.sma(close, 20)))
    strategy.entry("Long", strategy.long)
    
if (ta.crossunder(ta.sma(close, 10), ta.sma(close, 20)))
    strategy.close("Long")

// Webhook消息
if (strategy.position_changed)
    alert('{"token":"YOUR_TOKEN_HERE","ticker":"' + syminfo.ticker + '","action":"' + (strategy.position_size > 0 ? "buy" : "sell") + '","contracts":0.1,"price":"market"}', alert.freq_once_per_bar)`
                          navigator.clipboard.writeText(pineScript)
                          message.success('Pine Script已复制')
                        }}
                      >
                        复制
                      </Button>
                      <pre style={{ margin: 0, color: '#24292e' }}>
{`//@version=5
strategy("TradingBridge Webhook", overlay=true)

// 策略逻辑（示例）
if (ta.crossover(ta.sma(close, 10), ta.sma(close, 20)))
    strategy.entry("Long", strategy.long)
    
if (ta.crossunder(ta.sma(close, 10), ta.sma(close, 20)))
    strategy.close("Long")

// Webhook消息
if (strategy.position_changed)
    alert('{"token":"YOUR_TOKEN_HERE","ticker":"' + syminfo.ticker + '","action":"' + (strategy.position_size > 0 ? "buy" : "sell") + '","contracts":0.1,"price":"market"}', alert.freq_once_per_bar)`}
                      </pre>
                    </div>

                    <Title level={5} style={{ marginTop: 16 }}>动态变量示例</Title>
                    <div style={{ 
                      background: '#f6f8fa', 
                      padding: '12px', 
                      borderRadius: '6px',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      border: '1px solid #e1e4e8',
                      position: 'relative'
                    }}>
                      <Button 
                        type="text" 
                        size="small"
                        icon={<CopyOutlined />}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                        onClick={() => {
                          const dynamicExample = `{
  "token": "{{your_token_here}}",
  "ticker": "{{ticker}}",
  "action": "{{strategy.order.action}}",
  "contracts": {{strategy.order.contracts}},
  "price": {{close}},
  "sl": {{strategy.order.stop_loss}},
  "tp": {{strategy.order.take_profit}},
  "comment": "{{strategy.order.comment}}"
}`
                          navigator.clipboard.writeText(dynamicExample)
                          message.success('动态变量示例已复制')
                        }}
                      >
                        复制
                      </Button>
                      <pre style={{ margin: 0, color: '#24292e' }}>
{`{
  "token": "{{your_token_here}}",
  "ticker": "{{ticker}}",
  "action": "{{strategy.order.action}}",
  "contracts": {{strategy.order.contracts}},
  "price": {{close}},
  "sl": {{strategy.order.stop_loss}},
  "tp": {{strategy.order.take_profit}},
  "comment": "{{strategy.order.comment}}"
}`}
                      </pre>
                    </div>
                  </Col>

                  <Col xs={24} lg={10}>
                    <Title level={5}>配置步骤</Title>
                    <div style={{ background: '#f9f9f9', padding: 12, borderRadius: 6 }}>
                      <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                        <li style={{ marginBottom: 8 }}>
                          <Text strong>创建Token：</Text> 在Token管理页面创建新的API Token
                        </li>
                        <li style={{ marginBottom: 8 }}>
                          <Text strong>设置权限：</Text> 确保Token有交易权限和适当的风控限制
                        </li>
                        <li style={{ marginBottom: 8 }}>
                          <Text strong>配置Webhook：</Text> 在TradingView策略中添加alert()函数
                        </li>
                        <li style={{ marginBottom: 8 }}>
                          <Text strong>设置URL：</Text> 在Alert设置中填入Webhook URL
                        </li>
                        <li>
                          <Text strong>测试验证：</Text> 发送测试信号确认连接正常
                        </li>
                      </ol>
                    </div>

                    <Alert
                      type="warning"
                      showIcon
                      message="注意事项"
                      description={
                        <div style={{ fontSize: 12 }}>
                          <p>🔑 请妥善保管您的Token，不要泄露给他人</p>
                          <p>📊 建议先在测试环境验证策略逻辑</p>
                          <p>⚠️ 注意设置合理的仓位大小和风控参数</p>
                          <p>📈 建议在低流动性时段谨慎使用限价单</p>
                        </div>
                      }
                      style={{ marginTop: 16 }}
                    />

                    <div style={{ marginTop: 16, textAlign: 'center' }}>
                      <Button type="primary" href="/tokens" target="_blank">
                        创建 Token
                      </Button>
                      <Button style={{ marginLeft: 8 }} href="/settings" target="_blank">
                        配置交易所
                      </Button>
                    </div>
                  </Col>
                </Row>
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default Dashboard
